import { uiLocale } from '../i18n/language';

/**
 * Prompt Studio content (a prompt with its steps, a content block, a template, an evaluation
 * suite) in each language, as the API sends it: `{language: {original text: translated text}}`.
 * Filled in the background after a save, so any text may not be translated yet.
 */
export type ContentTranslations = Record<string, Record<string, string>>;

/**
 * `text` in the UI's language, or the original until its translation is ready.
 * Display only: anything sent back to the API (the prompt id, the generated prompt)
 * keeps the original, which is what the roleplay and its scoring run on.
 */
export function localizeContentText(translations: ContentTranslations | null | undefined, text: string): string {
  return translations?.[uiLocale()]?.[text] || text;
}

/** A name sent as `{language: name}` (e.g. `prompt_name_translations`), in the UI's language. */
export function localizeName(byLanguage: Record<string, string> | null | undefined, name: string): string {
  return byLanguage?.[uiLocale()] || name;
}

/**
 * `text`'s translation into the UI's language when that reads differently from it: what an
 * editor shows, and edits, in place of an original written in another language. `null` when
 * the original is already in the UI's language, or is not translated yet.
 */
export function editableTranslation(
  translations: ContentTranslations | null | undefined,
  text: string | null | undefined,
): string | null {
  if (!text) return null;
  const translated = translations?.[uiLocale()]?.[text];
  return translated && translated !== text ? translated : null;
}

/**
 * `text` (an original) in the other languages, for the hint under an editor field.
 * A field showing the original lists every translation that reads differently from it, so an
 * English prompt shows its Spanish. A field showing its translation into `shownLanguage` lists
 * every language but that one, the original's own included. Empty until translated, and as
 * soon as the original is edited away from what was translated.
 */
export function otherTranslations(
  translations: ContentTranslations | null | undefined,
  text: string | null | undefined,
  shownLanguage?: string | null,
): Array<{ language: string; text: string }> {
  const original = text?.trim();
  if (!translations || !text || !original) return [];
  return Object.entries(translations)
    .map(([language, byText]) => ({ language, text: byText[text] ?? byText[original] }))
    .filter((entry): entry is { language: string; text: string } =>
      !!entry.text && (shownLanguage ? entry.language !== shownLanguage : entry.text !== original));
}
